/**
 * Header Logo Alpine.js Component
 *
 * Handles header logo functionality including:
 * - Logo transition between default and scrolled states
 * - Scroll and header state detection
 * - Individual logo width configuration
 * - Smooth opacity transitions
 */
export default {
  name: 'headerLogo',
  component(settings) {
    return {
      // Reactive state
      isScrolled: false,

      // Event handlers for cleanup
      handleScroll: null,
      bodyObserver: null,
      headerObserver: null,

      init() {
        // Explicitly initialize as false to ensure proper initial state
        this.isScrolled = false;

        // Use nextTick to ensure DOM is ready before checking scroll state
        this.$nextTick(() => {
          this.checkScrollState();
        });

        // Only set up event listeners and height adjustment if scroll transition is enabled
        if (settings.enable_scroll_transition) {
          this.bindEvents();
          this.setupHeightAdjustment();
        }
      },

      bindEvents() {
        // Listen for scroll events
        this.handleScroll = () => this.checkScrollState();
        window.addEventListener('scroll', this.handleScroll, { passive: true });

        // Listen for body class changes (for header state changes)
        this.bodyObserver = new MutationObserver(() => this.checkScrollState());
        this.bodyObserver.observe(document.body, {
          attributes: true,
          attributeFilter: ['class']
        });
      },

      checkScrollState() {
        // Check actual scroll position instead of overlay header classes
        const scrollY = window.scrollY || window.pageYOffset;

        this.isScrolled =
          document.body.classList.contains('scrolled') ||
          document.body.classList.contains('header-hovered') ||
          scrollY > 0;
      },

      setupHeightAdjustment() {
        // Initial height adjustments
        this.$nextTick(() => {
          this.adjustHeaderHeight();
        });
      },

      adjustHeaderHeight() {
        if (!settings.enable_header_height_adjustment) return;

        const headerElement = this.$el.closest('header, .header-container, .header-overlay');
        if (!headerElement) return;

        const logoContainer = this.$el.querySelector('.header-logo-container');
        if (!logoContainer) return;

        // Use the container's height which will be determined by the visible logo
        let containerHeight = 0;

        // Small delay to ensure the logo has rendered
        this.$nextTick(() => {
          containerHeight = logoContainer.offsetHeight;

          if (containerHeight > 0) {
            // Apply height with some padding
            const padding = settings.header_height_padding || 20;
            const newHeight = containerHeight + padding;

            headerElement.style.minHeight = `${newHeight}px`;
            headerElement.style.transition = `min-height ${settings.transition_duration || 300}ms ease`;
          }
        });
      },

      // Cleanup method for when component is destroyed
      destroy() {
        if (this.handleScroll) {
          window.removeEventListener('scroll', this.handleScroll);
        }
        if (this.bodyObserver) {
          this.bodyObserver.disconnect();
        }
        if (this.headerObserver) {
          this.headerObserver.disconnect();
        }
      }
    };
  }
};
